// @flow

import React from 'react';
import Graph from './Graph';
import StrategyPercentageBar from '../../../components/StrategyPercentageBar';
import Legend from './Legend';
import {
  formatPostflopAction,
  getPostflopActionPotFraction,
  getPostflopRoundState,
  hasInvalidPostflopStrategy,
} from '../stepsHandler';

import styles from './styles.module.scss';

type Props = {
  currentStep: string,
  boardSituation: Array<any>,
  actionContext?: any,
}

const RangeGraph = ({ currentStep, boardSituation, actionContext = {} }: Props) => {
  const currentSituation = (step, situation) => {
    const res = situation.find((el) => el?.previous_actions === step);
    return res;
  };

  const getData = () => {
    const data = React.useMemo(() => currentSituation(currentStep, boardSituation),
      [currentStep, boardSituation]);
    return {
      fold: data?.fold || 0,
      bet: data?.bet || 0,
      check: data?.check || 0,
      raise: data?.raise || 0,
      call: data?.call || 0,
      big: data?.big || 0,
      small: data?.small || 0,
    };
  };

  const percentage = getData();
  const call = percentage?.call ? percentage?.call : 0;
  const check = percentage?.check ? percentage?.check : 0;
  const fold = percentage?.fold ? percentage?.fold : 0;
  const raise = percentage?.raise ? percentage?.raise : 0;
  const bet = percentage?.bet ? percentage?.bet : 0;
  const big = percentage?.big ? percentage?.big : 0;
  const small = percentage?.small ? percentage?.small : 0;
  const isResult = !call && !fold && !raise && !check && !bet && !big && !small;
  const currentActionContext = { ...actionContext, currentStep };
  const getSizingClassName = (action) => (
    getPostflopActionPotFraction(action, currentActionContext) > 1 / 2
      ? styles.big
      : styles.bet
  );
  const describeFrequency = (action, frequency) => {
    const label = formatPostflopAction(action, currentActionContext);
    const actionLabel = label.endsWith('%') ? `${label} pot` : label;
    return `${actionLabel}: ${frequency}% of hands`;
  };

  if (getPostflopRoundState(currentActionContext)?.complete || isResult) {
    return (
      <div className={styles.container} />
    );
  }

  if (hasInvalidPostflopStrategy(percentage, currentActionContext)) {
    return <div className={styles.container} role="status">Strategy unavailable for this action.</div>;
  }

  return (
    <div className={styles.container}>
      <div className={styles.headWrapper}>
        <div className={styles.legendWrapper}>
          { fold > 0 && <Legend text="Fold" className={`${styles.fold} ${styles.circleLegend}`} /> }
          { call > 0 && <Legend text="Call" className={`${styles.call} ${styles.circleLegend}`} /> }
          { check > 0 && <Legend text="Check" className={`${styles.call} ${styles.circleLegend}`} /> }
          { small > 0 && <Legend text={formatPostflopAction('small', currentActionContext)} className={`${getSizingClassName('small')} ${styles.circleLegend}`} /> }
          { bet > 0 && <Legend text={formatPostflopAction('bet', currentActionContext)} className={`${getSizingClassName('bet')} ${styles.circleLegend}`} /> }
          { raise > 0 && <Legend text={formatPostflopAction('raise', currentActionContext)} className={`${getSizingClassName('raise')} ${styles.circleLegend}`} /> }
          { big > 0 && <Legend text={formatPostflopAction('big', currentActionContext)} className={`${getSizingClassName('big')} ${styles.circleLegend}`} /> }
        </div>
      </div>
      <StrategyPercentageBar
        Graph={Graph}
        className={styles.graphWrapper}
        segments={[
          {
            id: 'fold', width: fold, className: styles.fold, label: describeFrequency('fold', fold),
          },
          {
            id: 'call', width: call, className: styles.call, label: describeFrequency('call', call),
          },
          {
            id: 'check', width: check, className: styles.call, label: describeFrequency('check', check),
          },
          {
            id: 'small', width: small, className: getSizingClassName('small'), label: describeFrequency('small', small),
          },
          {
            id: 'bet', width: bet, className: getSizingClassName('bet'), label: describeFrequency('bet', bet),
          },
          {
            id: 'raise', width: raise, className: getSizingClassName('raise'), label: describeFrequency('raise', raise),
          },
          {
            id: 'big', width: big, className: getSizingClassName('big'), label: describeFrequency('big', big),
          },
        ]}
      />

    </div>
  );
};

export default RangeGraph;
